// Phase 3B — relationship-driven "what to experience next".
//
// Each card states why it is being offered. Suggestions come from the authored
// ecosystem graph, the 47 Gates knowledge graph, or the experiential sequence —
// never from opaque scoring.
import { Link } from "@tanstack/react-router";
import { useReturnLoop } from "@/hooks/use-return-loop";
import { recommendNext } from "@/lib/recommend";
import { track } from "@/lib/analytics";

interface Props {
  heading?: string;
  limit?: number;
  surface?: string;
}

export function NextExperiences({
  heading = "Where this leads next",
  limit = 3,
  surface = "next_experiences",
}: Props) {
  const loop = useReturnLoop();
  if (!loop.ready) return null;

  const items = recommendNext({ memory: loop.memory, state: loop.state, limit });
  if (items.length === 0) return null;

  return (
    <section aria-label={heading} className="mt-16">
      <p className="text-xs uppercase tracking-[0.3em] text-gold">Continue the path</p>
      <h2 className="mt-2 font-serif text-2xl sm:text-3xl">{heading}</h2>
      <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((i) => (
          <li key={`${i.kind}:${i.ref}`}>
            <Link
              to={i.href as never}
              onClick={() =>
                track("journey_next_step_click", {
                  surface,
                  kind: i.kind,
                  ref: i.ref,
                  provenance: i.provenance,
                })
              }
              className="card-luxe hover-lift block h-full rounded-2xl border hairline bg-charcoal/40 p-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
            >
              <p className="font-serif text-lg text-ivory">{i.title}</p>
              <p className="mt-2 text-sm text-muted-foreground">{i.reason}</p>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
